import { defineStore } from "pinia";
import axios from "axios";
import { useAuthStore } from "./authStore";

export const useEventsStore = defineStore("events", {
  state: () => ({
    savedEvents: [], // Stores the events that have been successfully saved
    errors: [], // Holds error messages related to event actions
    isLoading: false, // Indicates whether an event-related request is in progress
    eventTypesWithBalance: [
      "Samtidighedsferieuger",
      "Særlige feriedage",
      "Søgnedage",
      "Friweekender",
    ], // Event types that affect the user's balance
    eventTypesWithoutBalance: [
      "Forlagt tjeneste",
      "Kursusvirksomhed",
      "Erstatningsfridag",
      "Sygdom",
    ], // Event types that do not affect the user's balance
    balanceUpdateSchedules: [], // Stores schedules for balance updates
  }),

  actions: {
    /**
     * Resets the current status by clearing errors and saved events.
     */
    resetStatus() {
      this.errors = [];
      this.savedEvents = [];
    },

    /**
     * Saves a list of events for a specific user.
     * @param {Array} events - The events to be saved.
     * @param {number|null} userId - The ID of the user (optional).
     * @returns {boolean} - Returns true if events are saved successfully, else false.
     */
    async saveEvents(events, userId = null) {
      this.resetStatus();
      this.isLoading = true;

      const authStore = useAuthStore();

      if (!authStore.isAuthenticated) {
        this.errors = ["Du skal være logget ind for at gemme fridage."];
        this.isLoading = false;
        return false;
      }

      try {
        const response = await axios.post(
          "my-plugin/v1/save-events",
          {
            events: events,
            user_id: userId,
          },
          {
            headers: {
              "X-WP-Nonce": authStore.wpNonce,
            },
            withCredentials: true,
          }
        );

        if (response.data.success) {
          this.savedEvents = response.data.saved_events;
          if (response.data.errors && response.data.errors.length > 0) {
            this.errors = response.data.errors;
            return false;
          }
          return true;
        } else {
          this.errors = response.data.errors || ["Ukendt fejl"];
          return false;
        }
      } catch (error) {
        console.error("Error saving events:", error);
        await this.handleError(error);
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Edits an existing event by replacing it with a new one.
     * @param {number} eventId - The ID of the event to edit.
     * @param {number} newReplacementId - The ID of the replacement event.
     * @returns {boolean} - Returns true if the event is edited successfully, else false.
     */
    async editEvent(eventId, newReplacementId) {
      this.resetStatus();
      this.isLoading = true;

      const authStore = useAuthStore();

      if (!authStore.isAuthenticated) {
        this.errors = ["Du skal være logget ind for at redigere fridage."];
        this.isLoading = false;
        return false;
      }

      try {
        const response = await axios.post(
          "my-plugin/v1/edit-event",
          {
            event_id: eventId,
            replacement_id: newReplacementId,
          },
          {
            headers: {
              "X-WP-Nonce": authStore.wpNonce,
            },
            withCredentials: true,
          }
        );

        if (response.data.success) {
          // Update the local state with the edited event
          const updatedEvent = response.data.event;
          const index = this.savedEvents.findIndex(
            (e) => e.id === updatedEvent.id
          );
          if (index !== -1) {
            this.savedEvents[index] = updatedEvent;
          } else {
            this.savedEvents.push(updatedEvent);
          }
          return true;
        } else {
          this.errors = [response.data.message || "Ukendt fejl"];
          return false;
        }
      } catch (error) {
        console.error("Error editing event:", error);
        await this.handleError(error);
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Removes an event from the user's saved events.
     * @param {Object} event - The event object to be removed.
     * @returns {boolean} - Returns true if the event is removed successfully, else false.
     */
    async removeEvent(event) {
      this.resetStatus();
      this.isLoading = true;

      const authStore = useAuthStore();

      if (!authStore.isAuthenticated) {
        this.errors = ["Du skal være logget ind for at fjerne fridage."];
        this.isLoading = false;
        return false;
      }

      try {
        const response = await axios.post(
          "my-plugin/v1/remove-event",
          {
            event_id: event.id,
          },
          {
            headers: {
              "X-WP-Nonce": authStore.wpNonce,
            },
            withCredentials: true,
          }
        );

        if (response.data.success) {
          // Remove all events with the same batch_id from the local state
          const batchId = event.batch_id;
          this.savedEvents = this.savedEvents.filter(e => e.batch_id !== batchId);
          return true;
        } else {
          this.errors = [response.data.message || "Ukendt fejl"];
          return false;
        }
      } catch (error) {
        console.error("Error removing event:", error);
        await this.handleError(error);
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Fetches the balance update schedules from the server.
     * @returns {boolean} - Returns true if schedules are fetched successfully, else false.
     */
    async fetchBalanceUpdateSchedules() {
      this.resetStatus();
      this.isLoading = true;

      const authStore = useAuthStore();

      if (!authStore.isAuthenticated) {
        this.errors = [
          "Du skal være logget ind for at hente saldoopdateringsskema.",
        ];
        this.isLoading = false;
        return false;
      }

      try {
        const response = await axios.get(
          "my-plugin/v1/balance-update-schedules",
          {
            headers: {
              "X-WP-Nonce": authStore.wpNonce,
            },
            withCredentials: true,
          }
        );

        if (response.data.success) {
          this.balanceUpdateSchedules = response.data.holiday_types;
          return true;
        } else {
          this.errors = [response.data.message || "Ukendt fejl"];
          return false;
        }
      } catch (error) {
        console.error("Error getting balance update schedules:", error);
        await this.handleError(error);
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Handles errors from Axios requests and sets appropriate error messages.
     * @param {Object} error - The error object returned by Axios.
     */
    async handleError(error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          console.log("Error response:", error.response);
          this.errors = [
            `Fejl ${error.response.status}: ${
              error.response.data.message ||
              "Der opstod en fejl under gemning af fridage."
            }`,
          ];
        } else if (error.request) {
          this.errors = [
            "Ingen respons fra serveren. Kontroller din internetforbindelse.",
          ];
        } else {
          this.errors = ["Der opstod en fejl ved forberedelse af anmodningen."];
        }
      } else {
        this.errors = ["Der opstod en uvendt fejl."];
      }
    },

    /**
     * Calculates the next occurrence of a specific date based on the current date.
     * @param {number} month - The month (1-12).
     * @param {number} day - The day of the month.
     * @returns {Date} - The next occurrence date.
     */
    getNextOccurrence(month, day) {
      const today = new Date();
      let nextOccurrence = new Date(today.getFullYear(), month - 1, day);

      // If the calculated date has already passed this year, set it to next year
      if (nextOccurrence < today) {
        nextOccurrence.setFullYear(today.getFullYear() + 1);
      }

      return nextOccurrence;
    },
  },

  getters: {
    /**
     * Checks if there are any saved events.
     * @param {Object} state - The store's state.
     * @returns {boolean} - True if there are saved events, else false.
     */
    hasSavedEvents: (state) => state.savedEvents.length > 0,

    /**
     * Checks if there are any errors present.
     * @param {Object} state - The store's state.
     * @returns {boolean} - True if there are errors, else false.
     */
    hasErrors: (state) => state.errors.length > 0,

    /**
     * Retrieves all event types by combining those with and without balance.
     * @param {Object} state - The store's state.
     * @returns {Array} - An array of all event types.
     */
    getAllEventTypes: (state) => [
      ...state.eventTypesWithBalance,
      ...state.eventTypesWithoutBalance,
    ],

    /**
     * Retrieves event types that affect the user's balance.
     * @param {Object} state - The store's state.
     * @returns {Array} - An array of event types with balance.
     */
    getEventTypesWithBalance: (state) => state.eventTypesWithBalance,

    /**
     * Retrieves event types that do not affect the user's balance.
     * @param {Object} state - The store's state.
     * @returns {Array} - An array of event types without balance.
     */
    getEventTypesWithoutBalance: (state) => state.eventTypesWithoutBalance,

    /**
     * Determines if a given event type affects the user's balance.
     * @param {Object} state - The store's state.
     * @returns {Function} - A function that takes an event type and returns a boolean.
     */
    isEventTypeWithBalance: (state) => (eventType) =>
      state.eventTypesWithBalance.includes(eventType),

    /**
     * Retrieves the balance update schedules.
     * @param {Object} state - The store's state.
     * @returns {Array} - An array of balance update schedules.
     */
    getBalanceUpdateSchedules: (state) => state.balanceUpdateSchedules,

    /**
     * Retrieves the formatted deadline date for a specific event type.
     * @param {Object} state - The store's state.
     * @returns {Function} - A function that takes an event type and returns a formatted date string.
     */
    getDeadline: (state) => (eventType) => {
      const balanceUpdateSchedule = state.balanceUpdateSchedules.find(
        (bu) => bu.type === eventType
      );

      if (balanceUpdateSchedule) {
        const startDate = new Date(balanceUpdateSchedule.start_date);
        startDate.setDate(startDate.getDate() - 1); // Subtract one day as per business logic
        const nextOccurrence = useEventsStore().getNextOccurrence(
          startDate.getMonth() + 1,
          startDate.getDate()
        );
        return nextOccurrence.toLocaleDateString("da-DK", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      }
      return null;
    },
  },
});